import React, { useEffect, useState, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import {
  X, CheckCircle2, AlertCircle, Loader2, ExternalLink,
  Trash2, RefreshCw, Building2, Users,
} from "lucide-react";
import { toast } from "sonner";

function BedeeLogoIcon({ size = 32 }) {
  return (
    <img
      src="https://app.bedee.cloud/favicon.svg"
      alt="Bedee"
      width={size}
      height={size}
      className="block"
      style={{ borderRadius: 6 }}
      onError={(e) => { e.target.style.display = "none"; }}
    />
  );
}

function StatBadge({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 border border-zinc-200 px-3 py-2 bg-zinc-50">
      <Icon size={13} strokeWidth={1.5} className="text-zinc-500 shrink-0" />
      <span className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">{label}</span>
      <span className="ml-auto text-[13px] font-mono font-semibold text-zinc-900">{value ?? "—"}</span>
    </div>
  );
}

export default function IntegrationsPanel() {
  const [config, setConfig] = useState({});
  const [stats, setStats] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://app.bedee.cloud/api/public");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testMsg, setTestMsg] = useState("");
  const [deleting, setDeleting] = useState(false);

  const bedeeCfg = config.bedee;
  const bedeeConnected = !!bedeeCfg?.api_key;

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get("/integrations/config");
      setConfig(r.data);
    } catch {}
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/integrations/bedee/stats");
      setStats(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadConfig();
    loadStats();
  }, [loadConfig, loadStats]);

  const openPanel = () => {
    setApiKey("");
    setBaseUrl(bedeeCfg?.base_url || "https://app.bedee.cloud/api/public");
    setTestResult(null);
    setTestMsg("");
    setPanelOpen(true);
  };

  const save = async () => {
    if (!apiKey.trim()) { toast.error("Saisissez une clé API."); return; }
    setSaving(true);
    try {
      await api.put("/integrations/bedee", { api_key: apiKey.trim(), base_url: baseUrl });
      toast.success("Clé API Bedee enregistrée.");
      await loadConfig();
      await loadStats();
      setPanelOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/integrations/bedee/test");
      setTestResult("ok");
      setTestMsg(r.data.message || "Connexion réussie.");
    } catch (err) {
      setTestResult("error");
      setTestMsg(getErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  const sync = async (forceFull = false) => {
    setSyncing(true);
    try {
      const r = await api.post("/integrations/bedee/sync", { force_full: forceFull });
      toast.success(r.data.message || "Synchronisation lancée en arrière-plan.");
      // Polling jusqu'à ce que last_sync_at change (la tâche de fond a terminé)
      const prevAt = stats?.last_sync_at;
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        const s = await api.get("/integrations/bedee/stats");
        setStats(s.data);
        if (s.data.last_sync_at !== prevAt || attempts >= 40) {
          clearInterval(poll);
          setSyncing(false);
        }
      }, 2000);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Déconnecter Bedee CRM ? Les données synchronisées seront conservées.")) return;
    setDeleting(true);
    try {
      await api.delete("/integrations/bedee");
      setConfig({});
      setStats(null);
      toast.success("Intégration Bedee supprimée.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  return (
    <div className="relative flex h-full">
      {/* Main grid */}
      <div className="flex-1 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-4">
          Intégrations disponibles
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

          {/* Bedee CRM card */}
          <div
            data-testid="integration-card-bedee"
            className="border border-zinc-200 bg-white hover:border-zinc-400 transition-colors cursor-pointer p-5 flex flex-col gap-3"
            onClick={openPanel}
          >
            <div className="flex items-center justify-between">
              <BedeeLogoIcon size={36} />
              {bedeeConnected ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                  <CheckCircle2 size={10} strokeWidth={2} /> Connecté
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-50 border border-zinc-200 px-2 py-0.5">
                  Non configuré
                </span>
              )}
            </div>
            <div>
              <div className="font-semibold text-zinc-900 text-sm">Bedee CRM</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Synchronisation des contacts et sociétés depuis votre CRM Bedee.
              </div>
            </div>

            {/* Stats inline on card */}
            {bedeeConnected && stats && (
              <div className="flex gap-3 text-[10px] font-mono text-zinc-500">
                <span className="flex items-center gap-1">
                  <Building2 size={10} strokeWidth={1.5} />
                  {stats.nb_entreprises} entreprises
                </span>
                <span className="flex items-center gap-1">
                  <Users size={10} strokeWidth={1.5} />
                  {stats.nb_contacts} contacts
                </span>
              </div>
            )}

            <div className="flex items-center gap-2 mt-auto pt-2 border-t border-zinc-100">
              <a
                href="https://app.bedee.cloud/api/public/docs"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-500 hover:text-zinc-800 transition-colors"
              >
                <ExternalLink size={10} strokeWidth={1.5} /> Documentation API
              </a>
              {bedeeConnected && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); sync(); }}
                    disabled={syncing}
                    data-testid="bedee-sync-card"
                    className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono text-[#002FA7] hover:underline transition-colors"
                  >
                    {syncing
                      ? <Loader2 size={10} strokeWidth={1.5} className="animate-spin" />
                      : <RefreshCw size={10} strokeWidth={1.5} />
                    }
                    Synchroniser
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); disconnect(); }}
                    disabled={deleting}
                    data-testid="bedee-disconnect"
                    className="inline-flex items-center gap-1 text-[10px] font-mono text-[#DC2626] hover:underline transition-colors"
                  >
                    {deleting
                      ? <Loader2 size={10} strokeWidth={1.5} className="animate-spin" />
                      : <Trash2 size={10} strokeWidth={1.5} />
                    }
                    Déconnecter
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Placeholder cards (coming soon) */}
          {["Google Drive", "HubSpot", "Salesforce"].map((name) => (
            <div key={name}
              className="border border-dashed border-zinc-200 bg-zinc-50/50 p-5 flex flex-col gap-3 opacity-50 cursor-not-allowed">
              <div className="w-9 h-9 bg-zinc-200" />
              <div>
                <div className="font-semibold text-zinc-500 text-sm">{name}</div>
                <div className="text-xs text-zinc-400 mt-0.5">Prochainement</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Side panel */}
      {panelOpen && (
        <aside
          data-testid="bedee-panel"
          className="w-[420px] border-l border-zinc-200 bg-white flex flex-col overflow-y-auto"
          style={{ minHeight: "100%" }}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-3">
            <BedeeLogoIcon size={28} />
            <div className="flex-1">
              <div className="font-semibold text-zinc-900 text-sm">Bedee CRM</div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                Configuration de l'intégration
              </div>
            </div>
            <button
              onClick={() => setPanelOpen(false)}
              className="text-zinc-400 hover:text-zinc-700 transition-colors"
              data-testid="bedee-panel-close"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 flex flex-col gap-5 flex-1">

            {/* Status + stats */}
            {bedeeConnected && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">
                  <CheckCircle2 size={13} strokeWidth={1.5} />
                  Intégration active — clé enregistrée ({bedeeCfg.api_key})
                </div>
                {stats && (
                  <div className="flex flex-col gap-1.5">
                    <StatBadge icon={Building2} label="Entreprises" value={stats.nb_entreprises} />
                    <StatBadge icon={Users} label="Contacts" value={stats.nb_contacts} />
                    {stats.last_sync_at && (
                      <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                        Dernière sync : {formatDate(stats.last_sync_at)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* API Key input */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
                Clé API Bedee
              </label>
              <input
                data-testid="bedee-api-key-input"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={bedeeConnected ? "Saisir une nouvelle clé pour remplacer…" : "Collez votre clé API ici"}
                className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-zinc-600"
              />
              <div className="mt-1.5 text-[10px] text-zinc-400 font-mono">
                Disponible dans votre espace Bedee → Paramètres → API.
              </div>
            </div>

            {/* Base URL (advanced) */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
                URL de base <span className="text-zinc-400">(avancé)</span>
              </label>
              <input
                data-testid="bedee-base-url-input"
                type="text"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                className="w-full border border-zinc-200 px-3 py-2 text-xs font-mono bg-zinc-50 focus:outline-none focus:border-zinc-400"
              />
            </div>

            {/* Test result */}
            {testResult && (
              <div className={`flex items-start gap-2 text-xs px-3 py-2 border ${
                testResult === "ok"
                  ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                  : "text-[#DC2626] bg-red-50 border-red-200"
              }`}>
                {testResult === "ok"
                  ? <CheckCircle2 size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  : <AlertCircle size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                }
                {testMsg}
              </div>
            )}

            {/* Sync result progress */}
            {syncing && (
              <div className="flex items-center gap-2 text-xs text-[#002FA7] bg-blue-50 border border-blue-200 px-3 py-2">
                <Loader2 size={13} strokeWidth={1.5} className="animate-spin shrink-0" />
                Synchronisation en cours…
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 mt-auto pt-4 border-t border-zinc-100">
              <button
                data-testid="bedee-test"
                onClick={test}
                disabled={testing || (!apiKey.trim() && !bedeeConnected)}
                className="w-full flex items-center justify-center gap-2 border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-50 transition-colors disabled:opacity-40"
              >
                {testing && <Loader2 size={13} strokeWidth={1.5} className="animate-spin" />}
                {testing ? "Test en cours…" : "Tester la connexion"}
              </button>

              {bedeeConnected && (
                <>
                  <button
                    data-testid="bedee-sync"
                    onClick={() => sync(false)}
                    disabled={syncing}
                    className="w-full flex items-center justify-center gap-2 border border-[#002FA7] text-[#002FA7] text-sm px-4 py-2 hover:bg-blue-50 transition-colors disabled:opacity-40"
                  >
                    {syncing
                      ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" />
                      : <RefreshCw size={13} strokeWidth={1.5} />
                    }
                    {syncing ? "Synchronisation…" : "Synchroniser (incrémental)"}
                  </button>
                  <button
                    data-testid="bedee-sync-full"
                    onClick={() => {
                      if (confirm("Resynchronisation complète : toutes les données Bedee seront rechargées depuis le début. Continuer ?")) {
                        sync(true);
                      }
                    }}
                    disabled={syncing}
                    className="w-full flex items-center justify-center gap-2 border border-zinc-400 text-zinc-700 text-sm px-4 py-2 hover:bg-zinc-50 transition-colors disabled:opacity-40"
                  >
                    <RefreshCw size={13} strokeWidth={1.5} />
                    Resync complet (repart de zéro)
                  </button>
                </>
              )}

              <button
                data-testid="bedee-save"
                onClick={save}
                disabled={saving || !apiKey.trim()}
                className="w-full flex items-center justify-center gap-2 bg-[#1A3A6E] hover:bg-[#112952] text-white text-sm font-medium px-4 py-2 transition-colors disabled:opacity-40"
              >
                {saving && <Loader2 size={13} strokeWidth={1.5} className="animate-spin" />}
                {saving ? "Enregistrement…" : "Enregistrer la clé"}
              </button>
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}
